import React, { useState } from 'react';
import { usePersistentState } from '../lib/usePersistentState';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { useUserData } from '../context/UserDataContext';
import EmptyState from './EmptyState';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

// College cost projector (5% annual inflation)
function collegeCost(yearsUntil) {
  const baseCosts = {
    inState:   28000,
    outState:  46000,
    private:   62000,
    ivy:       86000,
  };
  const inflate = Math.pow(1.05, yearsUntil);
  return {
    inState:  Math.round(baseCosts.inState * inflate),
    outState: Math.round(baseCosts.outState * inflate),
    private:  Math.round(baseCosts.private * inflate),
    ivy:      Math.round(baseCosts.ivy * inflate),
  };
}

// ── Child detail page ─────────────────────────────────────────────────────────
function ChildDetail({ member, onBack }) {
  const yearsUntil18 = 18 - member.age;
  const costs = collegeCost(yearsUntil18);
  const totalCost4yr = {
    inState:  costs.inState * 4,
    outState: costs.outState * 4,
    private:  costs.private * 4,
    ivy:      costs.ivy * 4,
  };

  const plan529 = member.plan529;
  const ugma = member.ugma;

  // Project 529 at 18 (7% annual return)
  const projectedWith529 = plan529
    ? Math.round(plan529.balance * Math.pow(1.07, yearsUntil18) + plan529.monthly * 12 * ((Math.pow(1.07, yearsUntil18) - 1) / 0.07))
    : 0;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-lg bg-slate-800 border border-slate-700 hover:bg-slate-700 transition-colors">
          <ArrowLeft size={16} className="text-slate-400" />
        </button>
        <div className="flex items-center gap-3">
          <span className="text-4xl">{member.avatar}</span>
          <div>
            <h2 className="text-xl font-bold text-slate-100">{member.name}</h2>
            <p className="text-slate-400 text-sm">Age {member.age} · {member.relation} · {yearsUntil18} years until college</p>
          </div>
        </div>
      </div>

      {/* Account summary */}
      <div className="grid grid-cols-2 gap-4">
        {plan529 && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">529 Plan</div>
            <div className="text-2xl font-bold text-blue-400 mb-1">{fmt(plan529.balance)}</div>
            <div className="text-xs text-slate-500">{plan529.institution}</div>
            <div className="text-xs text-slate-400 mt-2">{fmt(plan529.monthly)}/mo contribution</div>
            <div className="mt-3 pt-3 border-t border-slate-700">
              <div className="text-xs text-slate-500">Projected at age 18</div>
              <div className="text-lg font-bold text-emerald-400">{fmt(projectedWith529)}</div>
              <div className="text-xs text-slate-600">(7% avg annual return assumed)</div>
            </div>
          </div>
        )}
        {ugma && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">UGMA Account</div>
            <div className="text-2xl font-bold text-purple-400 mb-1">{fmt(ugma.balance)}</div>
            <div className="text-xs text-slate-500">{ugma.institution}</div>
            <div className="text-xs text-slate-400 mt-2">Holdings: {ugma.holdings}</div>
            <div className="mt-3 pt-3 border-t border-slate-700 text-xs text-slate-500">
              No contribution limit · Taxable · Becomes child's asset at 18/21
            </div>
          </div>
        )}
      </div>

      {/* College cost projector */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-200">College Cost Projector</h3>
          <span className="text-xs text-slate-500">5% annual tuition inflation · {yearsUntil18} years from now</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'In-State Public', annual: costs.inState, total: totalCost4yr.inState, color: 'text-emerald-400' },
            { label: 'Out-of-State', annual: costs.outState, total: totalCost4yr.outState, color: 'text-blue-400' },
            { label: 'Private University', annual: costs.private, total: totalCost4yr.private, color: 'text-amber-400' },
            { label: 'Ivy / Elite', annual: costs.ivy, total: totalCost4yr.ivy, color: 'text-rose-400' },
          ].map(c => (
            <div key={c.label} className="p-4 bg-slate-700/40 rounded-xl">
              <div className="text-xs text-slate-400 mb-1">{c.label}</div>
              <div className={`text-xl font-bold ${c.color}`}>{fmt(c.annual)}<span className="text-xs font-normal text-slate-500">/yr</span></div>
              <div className="text-xs text-slate-500 mt-1">4-year total: {fmt(c.total)}</div>
              {plan529 && (
                <div className={`text-xs mt-1 font-medium ${projectedWith529 >= c.total ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {projectedWith529 >= c.total
                    ? `✓ 529 covers ${Math.min(100, Math.round((projectedWith529 / c.total) * 100))}%`
                    : `Gap: ${fmt(c.total - projectedWith529)}`}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 529 vs UGMA explainer */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <h3 className="font-semibold text-slate-200 mb-4">529 vs UGMA/UTMA: Key Differences</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-2 text-xs text-slate-400 font-medium">Feature</th>
                <th className="text-left py-2 text-xs text-blue-400 font-medium">529 Plan</th>
                <th className="text-left py-2 text-xs text-purple-400 font-medium">UGMA/UTMA</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/40">
              {[
                ['Tax treatment', 'Tax-free growth + withdrawals (qualified)', 'Taxable — "kiddie tax" rules apply'],
                ['Use restriction', 'Education expenses only (K-12 + college)', 'No restrictions — child\'s money at 18/21'],
                ['FAFSA impact', '5.6% of parent asset (low impact)', '20% of student asset (high impact)'],
                ['Contribution limit', '$18k/yr gift tax limit ($90k front-load)', 'Unlimited (gift tax applies)'],
                ['SECURE 2.0 Roth', 'Can roll up to $35k → Roth IRA after 15 yrs', 'Not applicable'],
                ['Control', 'Parent controls account indefinitely', 'Child gets full control at majority'],
              ].map(([feat, p529, ugma]) => (
                <tr key={feat}>
                  <td className="py-2 text-xs text-slate-400">{feat}</td>
                  <td className="py-2 text-xs text-slate-300">{p529}</td>
                  <td className="py-2 text-xs text-slate-300">{ugma}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Adult detail page ─────────────────────────────────────────────────────────
function AdultDetail({ member, onBack }) {
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-lg bg-slate-800 border border-slate-700 hover:bg-slate-700 transition-colors">
          <ArrowLeft size={16} className="text-slate-400" />
        </button>
        <div className="flex items-center gap-3">
          <span className="text-4xl">{member.avatar}</span>
          <div>
            <h2 className="text-xl font-bold text-slate-100">{member.name}</h2>
            <p className="text-slate-400 text-sm">Age {member.age} · {member.relation}</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <div className="text-xs text-slate-400 uppercase tracking-wider mb-4">Financial Profile</div>
        <div className="space-y-3">
          {[
            { label: 'Annual Income', value: member.income ? fmt(member.income) : '—' },
            { label: 'Role in Household', value: member.relation },
            { label: 'Age', value: member.age },
          ].map(r => (
            <div key={r.label} className="flex justify-between py-2 border-b border-slate-700/50 last:border-0">
              <span className="text-sm text-slate-400">{r.label}</span>
              <span className="text-sm font-medium text-slate-200">{r.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-blue-950/30 border border-blue-700/30 rounded-xl p-5">
        <p className="text-sm text-blue-200/70">
          Extended profiles for household income, joint accounts, and individual retirement tracking coming in the next version.
        </p>
      </div>
    </div>
  );
}

// ── Add Member Modal ───────────────────────────────────────────────────────────
const AVATAR_OPTIONS = ['👨', '👩', '👧', '👦', '🧑', '👴', '👵'];
const RELATION_OPTIONS = ['You', 'Spouse/Partner', 'Son', 'Daughter', 'Other'];

function AddMemberModal({ onClose, onSave }) {
  const [name, setName] = useState('');
  const [relation, setRelation] = useState('');
  const [age, setAge] = useState('');
  const [avatar, setAvatar] = useState('👤');
  const [income, setIncome] = useState('');
  const [plan529Balance, setPlan529Balance] = useState('');
  const [plan529Monthly, setPlan529Monthly] = useState('');
  const [plan529Institution, setPlan529Institution] = useState('');
  const [ugmaBalance, setUgmaBalance] = useState('');
  const [ugmaInstitution, setUgmaInstitution] = useState('');
  const [ugmaHoldings, setUgmaHoldings] = useState('');

  const isChild = relation === 'Son' || relation === 'Daughter' || (age !== '' && Number(age) < 18);
  const isAdult = !isChild;

  function handleSave() {
    if (!name.trim() || !relation || age === '') return;

    const member = {
      id: Date.now().toString(),
      name: name.trim(),
      relation,
      age: Number(age),
      avatar,
    };

    if (isAdult && income !== '' && Number(income) > 0) {
      member.income = Number(income);
    }

    if (isChild) {
      if (Number(plan529Balance) > 0) {
        member.plan529 = {
          balance: Number(plan529Balance),
          monthly: Number(plan529Monthly) || 0,
          institution: plan529Institution.trim() || 'Unknown Institution',
        };
      }
      if (Number(ugmaBalance) > 0) {
        member.ugma = {
          balance: Number(ugmaBalance),
          institution: ugmaInstitution.trim() || 'Unknown Institution',
          holdings: ugmaHoldings.trim() || '',
        };
      }
    }

    onSave(member);
    onClose();
  }

  const inputCls = 'w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500';
  const labelCls = 'block text-xs text-slate-400 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-slate-100">Add Family Member</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
            <X size={16} className="text-slate-400" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Name */}
          <div>
            <label className={labelCls}>Name</label>
            <input
              className={inputCls}
              placeholder="Full name"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>

          {/* Relation */}
          <div>
            <label className={labelCls}>Relation</label>
            <select
              className={inputCls}
              value={relation}
              onChange={e => setRelation(e.target.value)}
            >
              <option value="">Select relation...</option>
              {RELATION_OPTIONS.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          {/* Age */}
          <div>
            <label className={labelCls}>Age</label>
            <input
              className={inputCls}
              type="number"
              min="0"
              max="120"
              placeholder="Age"
              value={age}
              onChange={e => setAge(e.target.value)}
            />
          </div>

          {/* Avatar */}
          <div>
            <label className={labelCls}>Avatar</label>
            <div className="flex gap-2 flex-wrap">
              {AVATAR_OPTIONS.map(em => (
                <button
                  key={em}
                  type="button"
                  onClick={() => setAvatar(em)}
                  className={`text-2xl p-2 rounded-lg border transition-colors ${
                    avatar === em
                      ? 'border-blue-500 bg-blue-500/20'
                      : 'border-slate-600 bg-slate-900 hover:border-slate-500'
                  }`}
                >
                  {em}
                </button>
              ))}
            </div>
          </div>

          {/* Annual Income (adults only) */}
          {isAdult && relation !== '' && (
            <div>
              <label className={labelCls}>Annual Income (optional)</label>
              <input
                className={inputCls}
                type="number"
                min="0"
                placeholder="e.g. 95000"
                value={income}
                onChange={e => setIncome(e.target.value)}
              />
            </div>
          )}

          {/* College Savings (children) */}
          {isChild && (
            <div className="space-y-3 pt-2">
              <div className="text-xs font-semibold text-slate-300 uppercase tracking-wider border-t border-slate-700 pt-3">
                College Savings (optional)
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>529 Balance</label>
                  <input
                    className={inputCls}
                    type="number"
                    min="0"
                    placeholder="e.g. 25000"
                    value={plan529Balance}
                    onChange={e => setPlan529Balance(e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelCls}>529 Monthly Contribution</label>
                  <input
                    className={inputCls}
                    type="number"
                    min="0"
                    placeholder="e.g. 300"
                    value={plan529Monthly}
                    onChange={e => setPlan529Monthly(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className={labelCls}>529 Institution</label>
                <input
                  className={inputCls}
                  placeholder="e.g. Vanguard 529"
                  value={plan529Institution}
                  onChange={e => setPlan529Institution(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>UGMA Balance</label>
                  <input
                    className={inputCls}
                    type="number"
                    min="0"
                    placeholder="e.g. 10000"
                    value={ugmaBalance}
                    onChange={e => setUgmaBalance(e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelCls}>UGMA Institution</label>
                  <input
                    className={inputCls}
                    placeholder="e.g. Fidelity"
                    value={ugmaInstitution}
                    onChange={e => setUgmaInstitution(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className={labelCls}>UGMA Holdings</label>
                <input
                  className={inputCls}
                  placeholder="e.g. VTI, VXUS"
                  value={ugmaHoldings}
                  onChange={e => setUgmaHoldings(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 border-t border-slate-700">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 rounded-lg border border-slate-600 text-sm text-slate-300 hover:bg-slate-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim() || !relation || age === ''}
            className="flex-1 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium text-white transition-colors"
          >
            Add Member
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Family list ───────────────────────────────────────────────────────────────
export default function Family() {
  const { familyData, updateSection } = useUserData();
  const [selected, setSelected] = usePersistentState('finovo_family_selected', null);
  const [showAdd, setShowAdd] = useState(false);

  const member = selected ? familyData.find(m => m.id === selected) : null;

  if (member) {
    const isChild = member.plan529 || member.ugma;
    return (
      <div className="p-6 max-w-4xl mx-auto">
        {isChild
          ? <ChildDetail member={member} onBack={() => setSelected(null)} />
          : <AdultDetail member={member} onBack={() => setSelected(null)} />
        }
      </div>
    );
  }

  function handleDelete(e, memberId) {
    e.stopPropagation();
    updateSection('family', familyData.filter(m => m.id !== memberId));
  }

  function handleAddMember(newMember) {
    updateSection('family', [...familyData, newMember]);
  }

  const kids = familyData.filter(m => m.plan529 || m.ugma);
  const totalSaved529 = kids.reduce((s, m) => s + (m.plan529?.balance || 0), 0);
  const totalSavedUGMA = kids.reduce((s, m) => s + (m.ugma?.balance || 0), 0);

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      {showAdd && (
        <AddMemberModal
          onClose={() => setShowAdd(false)}
          onSave={handleAddMember}
        />
      )}

      <div>
        <h1 className="text-2xl font-bold text-slate-100">Family</h1>
        <p className="text-slate-400 text-sm mt-0.5">Household members, college savings, and education planning</p>
      </div>

      {/* Empty state */}
      {familyData.length === 0 ? (
        <EmptyState
          icon="👨‍👩‍👧‍👦"
          title="No family members yet"
          description="Add your household members to track 529 plans, income, and college costs."
          actionLabel="Add Family Member"
          onAction={() => setShowAdd(true)}
        />
      ) : (
        <>
          {/* College savings summary */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Total in 529 Plans', value: fmt(totalSaved529), color: 'text-blue-400' },
              { label: 'Total in UGMA', value: fmt(totalSavedUGMA), color: 'text-purple-400' },
              { label: 'Combined Education', value: fmt(totalSaved529 + totalSavedUGMA), color: 'text-slate-100' },
            ].map(k => (
              <div key={k.label} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">{k.label}</div>
                <div className={`text-xl font-bold ${k.color}`}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* Family cards */}
          <div className="grid grid-cols-2 gap-4">
            {familyData.map(mem => {
              const isChild = !!(mem.plan529 || mem.ugma);
              const yearsTo18 = isChild ? 18 - mem.age : null;

              return (
                <div key={mem.id} className="relative group">
                  {/* Delete button */}
                  <button
                    onClick={(e) => handleDelete(e, mem.id)}
                    className="absolute top-3 right-3 z-10 p-1 rounded-md bg-slate-700 border border-slate-600 text-slate-400 hover:bg-rose-600/80 hover:border-rose-500 hover:text-white opacity-0 group-hover:opacity-100 transition-all"
                    title="Remove member"
                  >
                    <X size={12} />
                  </button>

                  <button
                    onClick={() => setSelected(mem.id)}
                    className="w-full bg-slate-800 border border-slate-700 hover:border-blue-600/50 rounded-xl p-5 text-left transition-all group/card"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-3xl">{mem.avatar}</span>
                      <div>
                        <div className="font-semibold text-slate-200 group-hover/card:text-blue-300 transition-colors">{mem.name}</div>
                        <div className="text-xs text-slate-500">{mem.relation} · Age {mem.age}</div>
                      </div>
                    </div>

                    {isChild ? (
                      <div className="space-y-2">
                        {mem.plan529 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">529 Plan</span>
                            <span className="font-semibold text-blue-400">{fmt(mem.plan529.balance)}</span>
                          </div>
                        )}
                        {mem.ugma && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">UGMA</span>
                            <span className="font-semibold text-purple-400">{fmt(mem.ugma.balance)}</span>
                          </div>
                        )}
                        <div className="pt-2 mt-2 border-t border-slate-700/50 text-xs text-slate-500">
                          {yearsTo18} years until college · Click for cost projector →
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {mem.income && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Annual Income</span>
                            <span className="font-semibold text-emerald-400">{fmt(mem.income)}</span>
                          </div>
                        )}
                        <div className="text-xs text-slate-500 pt-2 border-t border-slate-700/50 mt-2">
                          Click to view profile →
                        </div>
                      </div>
                    )}
                  </button>
                </div>
              );
            })}

            {/* Add member placeholder */}
            <button
              onClick={() => setShowAdd(true)}
              className="bg-slate-800/40 border-2 border-dashed border-slate-700 hover:border-blue-600/50 rounded-xl p-5 flex items-center justify-center gap-3 transition-all group"
            >
              <div className="w-10 h-10 rounded-full bg-slate-700 group-hover:bg-slate-600 flex items-center justify-center transition-all">
                <Plus size={18} className="text-slate-400" />
              </div>
              <div className="text-left">
                <div className="text-sm font-medium text-slate-400 group-hover:text-slate-200 transition-colors">Add family member</div>
                <div className="text-xs text-slate-600">Track their accounts & goals</div>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
